import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/ws";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, ShoppingBag, Wallet, CreditCard, Coins, AlertTriangle, Store, ArrowRight, Package, BookOpen, Users, ClipboardCheck, RefreshCw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");
const BRAND_COLORS = { "Pancong Lumer": "#F59E0B", "Takoyaki Sapore": "#EF4444", "Kopi Rimba": "#10B981", "Thai Smoothies": "#06B6D4", "Asinan Rujak": "#84CC16", "Toko Buah": "#A855F7", "Cilor Uhuy": "#EC4899" };

const ROLE_META = {
  owner: { title: "Business Overview", desc: "Ringkasan menyeluruh kondisi operasional dan keuangan Mazboy.", links: [{to:"/finance",label:"Finance"},{to:"/reports",label:"Laporan"},{to:"/hr",label:"People"},{to:"/inventory",label:"Stok"}] },
  manager: { title: "Operational Dashboard", desc: "Pantau performa outlet, penjualan, stok, dan tim dalam satu layar.", links: [{to:"/pos",label:"POS"},{to:"/inventory",label:"Stok"},{to:"/purchasing",label:"Purchasing"},{to:"/hr",label:"Tim"}] },
  supervisor: { title: "Outlet Dashboard", desc: "Fokus pada penjualan, stok, transaksi, dan kondisi outlet hari ini.", links: [{to:"/pos",label:"POS"},{to:"/inventory",label:"Stok"},{to:"/transactions",label:"Transaksi"},{to:"/hr",label:"Absensi"}] },
  keuangan: { title: "Finance Dashboard", desc: "Pantau hasil penjualan, gross profit, pengeluaran, dan kontrol keuangan.", links: [{to:"/finance",label:"Finance"},{to:"/reports",label:"Laporan"},{to:"/reconciliation",label:"Rekonsiliasi"},{to:"/payroll",label:"Payroll"}] },
  kasir: { title: "Kasir Dashboard", desc: "Akses cepat untuk transaksi penjualan dan pemeriksaan aktivitas kasir.", links: [{to:"/pos",label:"Buka POS"},{to:"/transactions",label:"Transaksi"}] },
  gudang: { title: "Warehouse Dashboard", desc: "Fokus pada stok kritis, transfer, purchasing, dan pergerakan barang.", links: [{to:"/inventory",label:"Inventory"},{to:"/transfers",label:"Transfer"},{to:"/purchasing",label:"Purchasing"}] },
};

function KPI({ label, value, icon: Icon, sub, tone = "orange", testid }) {
  const tones = { orange: "bg-orange-50 text-orange-700 border-orange-200", emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", blue: "bg-blue-50 text-blue-700 border-blue-200", rose: "bg-rose-50 text-rose-700 border-rose-200", slate: "bg-slate-50 text-slate-700 border-slate-200" };
  return <Card data-testid={testid} className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow"><CardContent className="p-4 sm:p-5"><div className="flex items-start justify-between mb-3"><span className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">{label}</span><div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${tones[tone]}`}><Icon className="w-4 h-4"/></div></div><div className="text-xl sm:text-2xl font-extrabold font-display num tracking-tight">{value}</div>{sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}</CardContent></Card>;
}


function CashierSelfDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [overtimes, setOvertimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const localDate = () =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });

  const [workDate, setWorkDate] = useState(localDate);
  const [workStart, setWorkStart] = useState("08:00");
  const [workEnd, setWorkEnd] = useState("17:00");
  const [workJobdesk, setWorkJobdesk] = useState("");

  const [otDate, setOtDate] = useState(localDate);
  const [otStart, setOtStart] = useState("17:00");
  const [otEnd, setOtEnd] = useState("19:00");
  const [otJobdesk, setOtJobdesk] = useState("");
  const [otType, setOtType] = useState("scheduled");

  const [leaveDate, setLeaveDate] = useState(localDate);
  const [leaveType, setLeaveType] = useState("day_off");
  const [leaveReason, setLeaveReason] = useState("");

  const attendance = data?.attendance;
  const workOpen = Boolean(attendance && !attendance.clock_out);
  const overtimeActive = activity?.mode === "overtime" && activity?.status === "active";

  const leaveLabels = {
    day_off: "Libur",
    annual: "Cuti tahunan",
    sick: "Sakit",
    unpaid: "Izin tanpa upah",
    other: "Lainnya",
  };

  const statusLabel = (value) => ({
    pending: "Menunggu persetujuan",
    approved: "Disetujui",
    rejected: "Ditolak",
    active: "Aktif",
    finished_pending_integration: "Selesai",
    finished: "Selesai",
  }[value] || value || "—");

  const showTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleTimeString("id-ID", {
      timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit"
    }) + " WIB";
  };

  const showDate = (value) => {
    if (!value) return "—";
    const d = new Date(`${String(value).slice(0, 10)}T12:00:00`);
    return Number.isNaN(d.getTime())
      ? value
      : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  };

  const fieldClass =
    "mt-1 w-full rounded-md border border-slate-300 bg-white p-2.5 text-base text-slate-900";
  const labelClass = "block text-sm font-medium text-slate-700";

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const results = await Promise.allSettled([
          api.get("/attendance/me"),
          api.get("/activity/me"),
          api.get("/leave-requests/me"),
        ]);
        if (!alive) return;

        if (results[0].status === "fulfilled") {
          const d = results[0].value.data;
          setData(d);
          if (d?.employee_id) {
            try {
              const ot = await api.get(
                `/overtime?employee_id=${encodeURIComponent(d.employee_id)}`
              );
              if (alive) setOvertimes(Array.isArray(ot.data) ? ot.data : (ot.data?.items || []));
            } catch {
              if (alive) setOvertimes([]);
            }
          }
        } else {
          setError(
            results[0].reason?.response?.data?.detail ||
            "Data absensi gagal dimuat."
          );
        }

        if (results[1].status === "fulfilled") {
          setActivity(results[1].value.data?.activity || null);
        }
        if (results[2].status === "fulfilled") {
          const value = results[2].value.data;
          setLeaves(Array.isArray(value) ? value : (value?.items || []));
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Data karyawan gagal dimuat.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [refreshKey]);

  async function startWork() {
    setError(""); setMessage("");
    if (workDate !== localDate()) {
      setError("Absensi masuk hanya dapat dicatat untuk hari ini.");
      return;
    }
    if (!workJobdesk.trim()) {
      setError("Isi jobdesk sebelum mulai kerja.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/attendance/me/clock-in", {
        note: `Jobdesk: ${workJobdesk.trim()} | Jadwal rencana: ${workStart}-${workEnd} | Tanggal: ${workDate}`
      });
      setMessage("Absensi masuk berhasil dicatat.");
      setRefreshKey(v => v + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal mencatat absensi masuk.");
    } finally {
      setBusy(false);
    }
  }

  async function endWork() {
    setError(""); setMessage(""); setBusy(true);
    try {
      await api.post("/attendance/me/clock-out", {
        note: `Jobdesk: ${workJobdesk.trim() || "Tidak diisi"} | Jadwal rencana: ${workStart}-${workEnd}`
      });
      setMessage("Absensi pulang berhasil dicatat.");
      setRefreshKey(v => v + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal mencatat absensi pulang.");
    } finally {
      setBusy(false);
    }
  }

  async function startOvertime() {
    setError(""); setMessage("");
    if (otDate !== localDate()) {
      setError("Sesi lembur hanya dapat dimulai untuk hari ini.");
      return;
    }
    if (!otJobdesk.trim()) {
      setError("Isi jobdesk lembur terlebih dahulu.");
      return;
    }
    if (otEnd <= otStart) {
      setError("Jam selesai lembur harus setelah jam mulai.");
      return;
    }
    if (workOpen) {
      setError("Catat pulang kerja reguler terlebih dahulu sebelum mulai lembur.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/activity/start", {
        mode: "overtime",
        planned_end: `${otDate}T${otEnd}:00`,
        work_description: `${otType === "urgent" ? "Lembur mendesak" : "Lembur terjadwal"} | Jadwal rencana ${otStart}-${otEnd} | ${otJobdesk.trim()}`
      });
      setMessage("Sesi lembur dimulai. Akhiri sesi untuk mengirim pengajuan persetujuan.");
      setRefreshKey(v => v + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal memulai sesi lembur.");
    } finally {
      setBusy(false);
    }
  }

  async function endOvertime() {
    if (!activity || !data?.employee_id) {
      setError("Data sesi lembur atau karyawan tidak tersedia.");
      return;
    }
    setError(""); setMessage(""); setBusy(true);
    const activeSession = activity;
    const startMs = Date.parse(activeSession.started_at || "");
    const elapsed = Number.isFinite(startMs)
      ? Math.min(12, Math.max(0.1, Math.round(((Date.now() - startMs) / 3600000) * 100) / 100))
      : 0.1;

    try {
      await api.post("/activity/finish", {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal mengakhiri sesi lembur.");
      setBusy(false);
      return;
    }

    try {
      await api.post("/overtime", {
        employee_id: data.employee_id,
        date: (activeSession.started_at || "").slice(0, 10) || otDate,
        hours: elapsed,
        reason: activeSession.work_description || otJobdesk.trim()
      });
      setMessage(`Sesi selesai. Pengajuan lembur ${elapsed} jam dikirim dan menunggu persetujuan.`);
    } catch (e) {
      setError(
        "Sesi lembur selesai, tetapi pengajuan lembur gagal dibuat: " +
        (e?.response?.data?.detail || "kesalahan server. Hubungi HR untuk pemeriksaan.")
      );
    } finally {
      setBusy(false);
      setRefreshKey(v => v + 1);
    }
  }

  async function submitLeave() {
    setError(""); setMessage("");
    if (!leaveDate || !leaveReason.trim()) {
      setError("Isi tanggal dan alasan libur/izin.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leave-requests", {
        date: leaveDate,
        leave_type: leaveType,
        reason: leaveReason.trim()
      });
      setLeaveReason("");
      setMessage("Pengajuan berhasil dikirim dan menunggu persetujuan.");
      setRefreshKey(v => v + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Pengajuan libur/izin gagal dikirim.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-shell space-y-5">
      <PageHeader
        eyebrow="Dashboard Karyawan"
        title={`Halo, ${user?.name || data?.employee_name || "Tim Mazboy"}`}
        description="Kelola absensi, lembur, dan pengajuan libur/izin dari satu halaman."
      />

      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {message && <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{message}</div>}
      {loading && <p className="text-sm text-slate-500">Memuat data karyawan…</p>}

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader>
            <CardTitle>Kerja</CardTitle>
            <p className="text-sm text-slate-500">Catat mulai dan selesai kerja reguler.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className={labelClass}>Tanggal kerja
              <input type="date" value={workDate} onChange={e => setWorkDate(e.target.value)} className={fieldClass}/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={labelClass}>Rencana masuk
                <input type="time" value={workStart} onChange={e => setWorkStart(e.target.value)} className={fieldClass}/>
              </label>
              <label className={labelClass}>Rencana pulang
                <input type="time" value={workEnd} onChange={e => setWorkEnd(e.target.value)} className={fieldClass}/>
              </label>
            </div>
            <label className={labelClass}>Jobdesk hari ini
              <textarea rows={3} value={workJobdesk} onChange={e => setWorkJobdesk(e.target.value)} placeholder="Contoh: persiapan outlet, pelayanan pelanggan, stok…" className={fieldClass}/>
            </label>
            <p className="text-xs text-slate-500">Jam absensi aktual tercatat otomatis saat tombol ditekan. Jam rencana dan jobdesk disimpan sebagai catatan.</p>
            {workOpen ? (
              <Button className="w-full" disabled={busy || loading} onClick={endWork}>
                {busy ? "Menyimpan…" : "Akhiri Kerja"}
              </Button>
            ) : (
              <Button className="w-full" disabled={busy || loading} onClick={startWork}>
                Mulai Kerja
              </Button>
            )}
            {attendance && <p className="text-sm text-slate-600">Masuk: {showTime(attendance.clock_in)} · Pulang: {showTime(attendance.clock_out)}</p>}
            {attendance?.clock_out && <p className="text-xs text-slate-500">Absensi reguler hari ini sudah selesai.</p>}
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader>
            <CardTitle>Lembur</CardTitle>
            <p className="text-sm text-slate-500">Sesi lembur akan dikirim sebagai pengajuan untuk ditinjau.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className={labelClass}>Tanggal lembur
              <input type="date" value={otDate} onChange={e => setOtDate(e.target.value)} className={fieldClass}/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={labelClass}>Rencana mulai
                <input type="time" value={otStart} onChange={e => setOtStart(e.target.value)} className={fieldClass}/>
              </label>
              <label className={labelClass}>Rencana selesai
                <input type="time" value={otEnd} onChange={e => setOtEnd(e.target.value)} className={fieldClass}/>
              </label>
            </div>
            <label className={labelClass}>Jenis lembur
              <select value={otType} onChange={e => setOtType(e.target.value)} className={fieldClass}>
                <option value="scheduled">Lembur terjadwal</option>
                <option value="urgent">Lembur mendesak</option>
              </select>
            </label>
            <label className={labelClass}>Jobdesk lembur
              <textarea rows={3} value={otJobdesk} onChange={e => setOtJobdesk(e.target.value)} placeholder="Jelaskan pekerjaan lembur…" className={fieldClass}/>
            </label>
            {overtimeActive ? (
              <>
                <p className="text-sm text-amber-700">Sesi lembur aktif sejak {showTime(activity.started_at)}.</p>
                <Button className="w-full" disabled={busy} onClick={endOvertime}>
                  {busy ? "Menyelesaikan…" : "Akhiri & Ajukan Lembur"}
                </Button>
              </>
            ) : (
              <Button className="w-full" disabled={busy || loading} onClick={startOvertime}>
                {busy ? "Memproses…" : "Mulai Lembur"}
              </Button>
            )}
            <p className="text-xs text-slate-500">Sesi lembur hanya dapat dimulai setelah absensi kerja reguler ditutup. Persetujuan tetap dilakukan HR/atasan.</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm md:col-span-2">
          <CardHeader>
            <CardTitle>Libur / Izin</CardTitle>
            <p className="text-sm text-slate-500">Ajukan libur atau izin dan pantau statusnya di bawah.</p>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <label className={labelClass}>Tanggal
              <input type="date" value={leaveDate} onChange={e => setLeaveDate(e.target.value)} className={fieldClass}/>
            </label>
            <label className={labelClass}>Jenis pengajuan
              <select value={leaveType} onChange={e => setLeaveType(e.target.value)} className={fieldClass}>
                {Object.entries(leaveLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className={`${labelClass} md:col-span-2`}>Alasan
              <textarea rows={3} value={leaveReason} onChange={e => setLeaveReason(e.target.value)} placeholder="Tuliskan alasan pengajuan…" className={fieldClass}/>
            </label>
            <div className="md:col-span-2">
              <Button className="w-full md:w-auto" disabled={busy || loading} onClick={submitLeave}>
                {busy ? "Mengirim…" : "Kirim Pengajuan"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200/80 shadow-sm">
        <CardHeader>
          <CardTitle>Riwayat Saya</CardTitle>
          <p className="text-sm text-slate-500">Ringkasan absensi hari ini serta riwayat lembur dan libur/izin.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <section>
            <h3 className="mb-2 font-semibold">Absensi hari ini</h3>
            {!attendance ? <p className="text-sm text-slate-500">Belum ada catatan absensi hari ini.</p> : (
              <div className="rounded-lg border p-3 text-sm">
                <p>Tanggal: {showDate(data?.date || localDate())}</p>
                <p>Masuk: {showTime(attendance.clock_in)}</p>
                <p>Pulang: {showTime(attendance.clock_out)}</p>
                <p>Status: {workOpen ? "Sedang bekerja" : "Selesai"}</p>
              </div>
            )}
            {activity && <p className="mt-2 text-sm text-slate-600">Aktivitas: {activity.mode || "—"} · {statusLabel(activity.status)}</p>}
          </section>

          <section>
            <h3 className="mb-2 font-semibold">Riwayat lembur</h3>
            {overtimes.length === 0 ? <p className="text-sm text-slate-500">Belum ada pengajuan lembur.</p> : (
              <div className="space-y-2">
                {overtimes.slice(0, 20).map((item, i) => (
                  <div key={item.id || item._id || `${item.date}-${i}`} className="rounded-lg border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{showDate(item.date)}</strong>
                      <Badge>{statusLabel(item.status)}</Badge>
                    </div>
                    <p className="mt-1">{item.hours ?? "—"} jam · {item.reason || "Tanpa keterangan"}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-2 font-semibold">Riwayat libur / izin</h3>
            {leaves.length === 0 ? <p className="text-sm text-slate-500">Belum ada pengajuan libur/izin.</p> : (
              <div className="space-y-2">
                {leaves.slice(0, 30).map((item, i) => (
                  <div key={item.id || item._id || `${item.date}-${i}`} className="rounded-lg border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{showDate(item.date)} · {leaveLabels[item.leave_type] || item.leave_type || "Pengajuan"}</strong>
                      <Badge>{statusLabel(item.status)}</Badge>
                    </div>
                    <p className="mt-1">{item.reason || "Tanpa alasan"}</p>
                    {item.decision_reason && <p className="mt-1 text-slate-500">Catatan keputusan: {item.decision_reason}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>

          <Button variant="outline" disabled={loading} onClick={() => setRefreshKey(v => v + 1)}>
            Muat Ulang Riwayat
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [sum, setSum] = useState(null); const [trend, setTrend] = useState([]); const [tops, setTops] = useState([]); const [txs, setTxs] = useState([]); const [loading, setLoading] = useState(true); const [error, setError] = useState("");
  const roleKey = user?.role === "cashier_outlet" ? "kasir" : user?.role;
  const meta = ROLE_META[roleKey] || ROLE_META.kasir;

  const isCashier = user?.role === "cashier_outlet" || user?.role === "kasir";

  const fetchAll = useCallback(async () => {
    if (isCashier) { setLoading(false); return; }
    try { setError(""); const [s,t,p,tx] = await Promise.all([api.get("/dashboard/summary"),api.get("/dashboard/sales-trend?days=7"),api.get("/dashboard/top-products?limit=6"),api.get("/transactions?limit=8")]); setSum(s.data); setTrend(t.data); setTops(p.data); setTxs(tx.data); }
    catch (e) { setError(e?.response?.data?.detail || "Dashboard gagal dimuat"); }
    finally { setLoading(false); }
  }, [isCashier]);
  useEffect(() => {
    if (isCashier) { setLoading(false); return; }
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll, isCashier]);
  useRealtime((msg) => { if (["transaction.created","transaction.paid","payment.paid","expense.created"].includes(msg.event)) { clearTimeout(window.__mazboyDashDebounce); window.__mazboyDashDebounce = setTimeout(fetchAll, 500); } });

  const pieData = useMemo(() => Object.entries(sum?.today?.by_method || {}).map(([k,v]) => ({name:k.toUpperCase(),value:v})), [sum]);
  if (isCashier) return <CashierSelfDashboard />;
  if (loading) return <div className="page-shell"><div className="skeleton-panel h-28"/><div className="grid grid-cols-2 lg:grid-cols-4 gap-4"><div className="skeleton-panel h-28"/><div className="skeleton-panel h-28"/><div className="skeleton-panel h-28"/><div className="skeleton-panel h-28"/></div><div className="skeleton-panel h-72"/></div>;
  if (error || !sum) return <div className="page-shell"><Card><EmptyState title="Dashboard belum tersedia" description={error || "Data dashboard belum dapat dimuat."} action={<Button onClick={()=>{setLoading(true);fetchAll();}}><RefreshCw className="w-4 h-4 mr-2"/>Coba lagi</Button>}/></Card></div>;

  const t = sum.today || {};
  return <div className="page-shell">
    <PageHeader eyebrow={user?.role ? `${meta.title} · ${user.role}` : "Overview"} title={`Selamat datang, ${user?.name || "Tim Mazboy"}`} description={meta.desc} status="Data real-time" actions={<div className="flex flex-wrap gap-2">{meta.links.map(x=><Button key={x.to} asChild variant="outline" size="sm"><Link to={x.to}>{x.label}<ArrowRight className="w-3.5 h-3.5 ml-1"/></Link></Button>)}</div>}/>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <KPI testid="kpi-omzet" label="Omzet Hari Ini" value={rupiah(t.sales)} icon={TrendingUp} sub={`${t.trx_count || 0} transaksi`} tone="orange"/>
      <KPI testid="kpi-profit" label="Gross Profit" value={rupiah(t.gross_profit)} icon={Coins} sub={`${Number(t.margin || 0).toFixed(1)}% margin`} tone="emerald"/>
      <KPI testid="kpi-avg" label="Avg Basket" value={rupiah(t.avg_ticket)} icon={ShoppingBag} sub={`HPP ${rupiah(t.hpp)}`} tone="blue"/>
      <KPI testid="kpi-expense" label="Pengeluaran" value={rupiah(t.expenses)} icon={Wallet} sub={`Net ${rupiah(t.net)}`} tone="rose"/>
    </div>

    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="dashboard-chart-card lg:col-span-2 border-slate-200/80 shadow-sm"><CardHeader className="pb-2"><CardTitle className="text-base">Sales Trend · 7 Hari</CardTitle><p className="text-xs text-slate-500">Omzet dan gross profit</p></CardHeader><CardContent><div className="h-64"><ResponsiveContainer><LineChart data={trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/><XAxis dataKey="date" fontSize={11} stroke="#64748b"/><YAxis fontSize={11} stroke="#64748b" tickFormatter={v=>"Rp"+(v/1000)+"k"}/><Tooltip formatter={v=>rupiah(v)}/><Line type="monotone" dataKey="sales" stroke="#F97316" strokeWidth={2.5} dot={{r:3}}/><Line type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2} dot={{r:3}}/></LineChart></ResponsiveContainer></div></CardContent></Card>
      <Card className="dashboard-chart-card border-slate-200/80 shadow-sm"><CardHeader className="pb-2"><CardTitle className="text-base">Metode Pembayaran</CardTitle></CardHeader><CardContent><div className="h-64">{pieData.length ? <ResponsiveContainer><PieChart><Pie data={pieData} innerRadius={52} outerRadius={88} dataKey="value" nameKey="name">{pieData.map((_,i)=><Cell key={i} fill={["#F97316","#10B981","#3B82F6","#EF4444"][i%4]}/>)}</Pie><Tooltip formatter={v=>rupiah(v)}/></PieChart></ResponsiveContainer> : <EmptyState title="Belum ada transaksi" description="Metode pembayaran akan muncul setelah transaksi tercatat."/>}</div></CardContent></Card>
    </div>

    <Card className="border-slate-200/80 shadow-sm"><CardHeader className="pb-2 flex-row items-center justify-between"><div><CardTitle className="text-base flex items-center gap-2"><Store className="w-4 h-4 text-orange-500"/>Live Outlet Monitor</CardTitle><p className="text-xs text-slate-500 mt-1">Ringkasan penjualan per outlet</p></div><span className="text-xs text-emerald-600 pulse-live">Live</span></CardHeader><CardContent><div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">{(sum.outlets||[]).map(o=><div key={o.outlet_id} data-testid={`outlet-tile-${o.outlet_id}`} className="p-4 rounded-xl border bg-white hover:shadow-md transition"><div className="flex items-center gap-2 mb-2"><div className="brand-dot" style={{"--brand":"0 0% 50%",background:BRAND_COLORS[o.brand]||"#F97316"}}/><span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 truncate">{o.brand}</span></div><div className="font-semibold text-sm truncate">{o.name}</div><div className="mt-3 flex items-end justify-between gap-2"><div><div className="text-lg font-extrabold num">{rupiah(o.sales)}</div><div className="text-xs text-slate-500">{o.trx_count} trx · profit {rupiah(o.profit)}</div></div><Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">OPEN</Badge></div></div>)}{!(sum.outlets||[]).length && <div className="sm:col-span-2 lg:col-span-4"><EmptyState title="Belum ada outlet" description="Data outlet akan tampil setelah master outlet tersedia."/></div>}</div></CardContent></Card>

    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="border-slate-200/80 shadow-sm"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4 text-orange-500"/>Top Produk · 30 Hari</CardTitle></CardHeader><CardContent><div className="h-64">{tops.length ? <ResponsiveContainer><BarChart data={tops} layout="vertical"><XAxis type="number" fontSize={11} stroke="#64748b"/><YAxis dataKey="name" type="category" fontSize={11} stroke="#64748b" width={130}/><Tooltip formatter={v=>rupiah(v)}/><Bar dataKey="revenue" fill="#F97316" radius={[0,6,6,0]}/></BarChart></ResponsiveContainer> : <EmptyState title="Belum ada penjualan" description="Produk terlaris akan muncul setelah ada transaksi."/>}</div></CardContent></Card>
      <Card className="border-slate-200/80 shadow-sm"><CardHeader className="pb-2 flex-row items-center justify-between"><div><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-500"/>Stok Kritis</CardTitle><p className="text-xs text-slate-500 mt-1">Item yang perlu diperiksa</p></div><Badge variant="destructive">{sum.critical_stock?.length || 0}</Badge></CardHeader><CardContent><div className="space-y-2 max-h-64 overflow-auto">{sum.critical_stock?.length ? sum.critical_stock.map(p=><div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-rose-50 border border-rose-100"><div><div className="text-sm font-semibold">{p.name}</div><div className="text-xs text-slate-500">{p.brand}</div></div><Badge variant="destructive" className="num">{p.stock} {p.unit}</Badge></div>) : <EmptyState title="Stok aman" description="Tidak ada item di bawah batas minimum."/>}</div></CardContent></Card>
    </div>

    <Card className="border-slate-200/80 shadow-sm"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4 text-orange-500"/>Transaksi Terbaru</CardTitle></CardHeader><CardContent><div className="divide-y">{txs.slice(0,8).map(x=><div key={x.id} className="py-3 flex items-center justify-between gap-3 text-sm"><div className="min-w-0"><div className="font-mono text-[11px] text-slate-500">{x.trx_no}</div><div className="font-medium truncate">{x.items?.length || 0} item · {x.cashier_name}</div></div><div className="text-right shrink-0"><div className="font-extrabold num">{rupiah(x.total)}</div><Badge variant="outline" className="text-[10px] mt-0.5">{x.payment_method?.toUpperCase()} · {x.status}</Badge></div></div>)}{!txs.length && <EmptyState title="Belum ada transaksi" description="Transaksi baru akan muncul di sini."/>}</div></CardContent></Card>
  </div>;
}
